import { Box, FormControl } from '@mui/material';
import { useCallback } from 'react';
import { isEqual } from 'lodash';
import FieldLabel from '../../common/FieldLabel';
import { useSelector } from 'react-redux';
import {
  selectSlackIntegrationStatus,
  SlackConnectionStatus,
} from '../../../reducers/integrationsReducer';
import { Typography, colors } from '@sweep-io/sweep-design';
import { SlackNotConnected } from './SlackNotConnected';
import { SlackRecipientsSelect } from './SlackRecipientsSelect';
import { useSlackRecipients } from './useSlackRecipients';
import { SlackNewMessageEditorLexical as SlackNewMessageEditor } from './SlackNewMessageEditorLexical';
import { useGetSlackRecipientsLabels } from './useGetSlackRecipientsLabels';
import { SlackConstant } from './utils';
import { SlackSelectDealroomType } from './SlackSelectDealroomType';
import { SlackAdditionalFieldsTable } from './SlackAdditionalFieldsTable';

interface SlackAddMemberActionProps {
  readonly?: boolean;
  action: SlackAddMemberAutomationAction;
  crmOrgId: string;
  onChange: (slackDealRoomAutomationAction: SlackAddMemberAutomationAction) => any;
  objectName: string;
  showAssignmentSelector?: boolean;
}

export const SlackAddMemberAction = ({
  readonly,
  action,
  crmOrgId,
  objectName,
  onChange,
  showAssignmentSelector,
}: SlackAddMemberActionProps) => {
  const slackStatus = useSelector(selectSlackIntegrationStatus);
  const { recipientWithLabelsArray } = useGetSlackRecipientsLabels({
    recipients: action.actionParams?.members ?? [],
  });
  readonly = readonly || slackStatus !== SlackConnectionStatus.Connected;

  const updateOnChange = useCallback(
    (updateProps: Partial<AutomationSlackAddMemberActionParams>) => {
      onChange({
        ...action,
        actionParams: {
          ...action.actionParams,
          ...updateProps,
        },
      });
    },
    [action, onChange],
  );

  const setBeforeCallingFields = useCallback(() => {
    updateOnChange({
      fields: [...(action.actionParams?.fields || [])],
      channelRef: action.actionParams?.channelRef
        ? { ...action.actionParams?.channelRef }
        : {
            type: SlackConstant.Sweep_Created_Slack_Channel,
          },
    });
  }, [action.actionParams?.channelRef, action.actionParams?.fields, updateOnChange]);

  const { isLoading } = useSlackRecipients({
    objectName,
    crmOrgId,
    setBeforeCallingFields,
  });

  const handleDeleteChip = useCallback(
    (chipToRemove: SlackRecipientStruct) => {
      const newRecipients = action.actionParams.members?.filter((item) => {
        if (chipToRemove.type === SlackConstant.Slack && item.type === SlackConstant.Slack) {
          return item.value !== chipToRemove.value;
        }
        if (
          chipToRemove.type === SlackConstant.Assignment &&
          item.type === SlackConstant.Assignment
        ) {
          return item.value !== chipToRemove.value;
        }
        if (
          (chipToRemove.type === SlackConstant.Salesforce ||
            chipToRemove.type === SlackConstant.Salesforce_Created_Slack_Channel) &&
          item.type === chipToRemove.type
        ) {
          return !isEqual(
            (item.value as SlackFieldsStruct).fieldIds,
            (chipToRemove.value as SlackFieldsStruct).fieldIds,
          );
        }
        return true;
      });
      updateOnChange({ members: newRecipients });
    },
    [updateOnChange, action],
  );

  const _onChange = useCallback(
    (event: any, newValues: any) => {
      const newItems = newValues.map((el: any) => {
        if (el.type === SlackConstant.Salesforce) {
          return {
            ...el,
            type: SlackConstant.Salesforce,
            value: {
              fieldIds: el.value.fieldIds,
            },
          };
        } else if (el.type === SlackConstant.Salesforce_Created_Slack_Channel) {
          return {
            ...el,
            type: SlackConstant.Salesforce_Created_Slack_Channel,
            value: {
              fieldIds: el.value.fieldIds,
            },
          };
        } else if (el.type === SlackConstant.Assignment) {
          return {
            ...el,
            type: SlackConstant.Assignment,
            value: el.value,
          };
        } else {
          return {
            ...el,
            type: SlackConstant.Slack,
            value: el.value,
          };
        }
      });
      updateOnChange({ members: newItems as SlackRecipientStruct[] });
    },
    [updateOnChange],
  );

  return (
    <>
      {slackStatus !== SlackConnectionStatus.Connected && (
        <Box sx={{ marginTop: '32px' }}>
          <SlackNotConnected slackStatus={slackStatus} />
        </Box>
      )}

      {(slackStatus === SlackConnectionStatus.Connected || action?.actionParams?.members) && (
        <Box
          sx={{
            background: colors.grey[100],
            padding: '24px 24px 19px',
            borderRadius: '8px',
            margin: '16px 0',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <SlackSelectDealroomType
            readonly={readonly}
            value={action?.actionParams?.channelRef}
            crmOrgId={crmOrgId}
            objectName={objectName}
            onChange={(val) => {
              updateOnChange({ channelRef: val });
            }}
          />

          <FormControl
            sx={{
              width: '100%',
              marginBottom: '20px',
              maxWidth: '1148px',
            }}
          >
            <Box
              sx={{
                marginBottom: '3px',
                '& div': {
                  color: colors.grey[800],
                  fontSize: '12px',
                  fontWeight: '400',
                },
              }}
            >
              <FieldLabel label={'Add members'} markLabelAsRequired={true} />
            </Box>

            <SlackRecipientsSelect
              customButtonText={'Add from Salesforce field'}
              objectName={objectName}
              crmOrgId={crmOrgId}
              isLoading={isLoading}
              onChange={_onChange}
              hideDisclaimer={true}
              readonly={!!readonly}
              recipientsNoChannels
              recipients={recipientWithLabelsArray}
              handleDeleteChip={handleDeleteChip}
              showAssignmentSelector={showAssignmentSelector}
            />
          </FormControl>

          <SlackNewMessageEditor
            placeholder="Type message body..."
            isRequired={false}
            label="Message"
            readonly={readonly}
            messageStruct={action.actionParams?.message ?? { template: '', variables: [] }}
            crmOrgId={crmOrgId}
            onChange={(newMsg) => {
              updateOnChange({ message: newMsg });
            }}
            objectName={objectName}
            showAssignmentSelector={showAssignmentSelector}
          />

          <SlackAdditionalFieldsTable
            readonly={readonly}
            fields={action.actionParams?.fields}
            updateOnChange={(fields) => {
              updateOnChange({ fields });
            }}
            crmOrgId={crmOrgId}
            objectName={objectName}
          />

          <Typography variant="body">
            Note: this automation would work as expected only if the record has a dedicated deal
            room
          </Typography>
        </Box>
      )}
    </>
  );
};
